import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Save, Cloud, Server, ExternalLink } from 'lucide-react';
import { locationService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { nvrHttpUrl } from '../../utils/nvrAccess';

type ProviderType = 'HIKVISION' | 'DAHUA' | 'UBIQUITI' | 'CUSTOM_NVR';

const CreateLocationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [provider, setProvider] = useState<ProviderType>('CUSTOM_NVR');
  const [formData, setFormData] = useState({
    ycl_name: '',
    ycl_code: '',
    ycl_type: '',
    ycl_description: '',
    ycl_address: '',
    ycl_city: '',
    ycl_province: '',
    ycl_postal_code: '',
    ycl_latitude: '',
    ycl_longitude: '',
    ycl_caller_id: '',
    // Cloud Integration
    api_key: '',
    app_secret: '',
    verification_code: '', // HikVision verification code for EZOPEN plugin
    // NVR Configuration
    nvr_ip: '',
    nvr_port: '',
    nvr_rtsp_port: '',
    nvr_username: '',
    nvr_password_encrypted: '',
    nvr_type: '',
    nvr_model: '',
    nvr_channel_count: '',
    transcoding_enabled: false,
    transcoding_device_ip: '',
    transcoding_device_port: '',
    transcoding_device_api_port: '',
    transcoding_device_username: '',
    transcoding_device_password_encrypted: '',
    external_hostname: '',
    external_http_port: '80',
    external_transcoding_port: '',
    external_rtsp_port: '',
    external_access_enabled: false,
    stream_auth_enabled: false,
    stream_auth_port: '8000',
    location_identifier: '',
    stream_shared_secret: '',
    ycl_company: '',
    ycl_events_enabled: true,
    ycl_entry_logging: true,
    ycl_defaut_location: false,
    status: true,
    location_timezone: 'America/Toronto',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Set company ID from auth context on mount
  useEffect(() => {
    if (user.companyId) {
      setFormData(prev => ({ ...prev, ycl_company: String(user.companyId) }));
    }
  }, [user.companyId]);

  // Clear form errors when provider changes
  useEffect(() => {
    setFormErrors({});
    setError(null);
  }, [provider]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Always required
    if (!formData.ycl_name.trim()) errors.ycl_name = 'Location name is required';
    if (!formData.ycl_code.trim()) errors.ycl_code = 'Location code is required';
    if (!formData.ycl_type.trim()) errors.ycl_type = 'Location type is required';
    
    // Cloud provider validation (HIKVISION, DAHUA, or UBIQUITI)
    if (provider === 'HIKVISION' || provider === 'DAHUA' || provider === 'UBIQUITI') {
      if (!formData.api_key.trim()) errors.api_key = 'API Key is required for cloud providers';
      if (!formData.app_secret.trim()) errors.app_secret = 'App Secret is required for cloud providers';
      if (provider === 'HIKVISION' && !formData.verification_code.trim()) {
        errors.verification_code = 'Verification code is required for HikVision EZOPEN plugin';
      }
    }
    
    // Custom NVR validation
    if (provider === 'CUSTOM_NVR') {
      if (!formData.nvr_ip.trim()) errors.nvr_ip = 'NVR IP address is required';
      if (!formData.nvr_username.trim()) errors.nvr_username = 'NVR username is required';
      if (!formData.nvr_password_encrypted.trim()) errors.nvr_password_encrypted = 'NVR password is required';
    }
    
    if (formData.external_access_enabled) {
      try { nvrHttpUrl(formData.external_hostname, formData.external_http_port, formData.nvr_port); }
      catch (error) { errors.external_hostname = error instanceof Error ? error.message : 'Check the NVR HTTP/ISAPI address.'; }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setError(null);
      setSuccess(null);
      
      // Clean and convert data
      const payload: any = {
        ycl_name: formData.ycl_name.trim(),
        ycl_code: formData.ycl_code.trim(),
      };

      // Add optional string fields (only if not empty)
      if (formData.ycl_type) payload.ycl_type = formData.ycl_type;
      if (formData.ycl_description) payload.ycl_description = formData.ycl_description;
      if (formData.ycl_address) payload.ycl_address = formData.ycl_address;
      if (formData.ycl_city) payload.ycl_city = formData.ycl_city;
      if (formData.ycl_province) payload.ycl_province = formData.ycl_province;
      if (formData.ycl_postal_code) payload.ycl_postal_code = formData.ycl_postal_code;
      if (formData.ycl_caller_id) payload.ycl_caller_id = formData.ycl_caller_id;

      // Add numeric fields (parse and validate)
      if (formData.ycl_latitude) {
        const lat = parseFloat(formData.ycl_latitude);
        if (!isNaN(lat)) payload.ycl_latitude = lat;
      }
      if (formData.ycl_longitude) {
        const lng = parseFloat(formData.ycl_longitude);
        if (!isNaN(lng)) payload.ycl_longitude = lng;
      }
      if (formData.ycl_company) {
        const company = parseInt(formData.ycl_company);
        if (!isNaN(company)) payload.ycl_company = company;
      }

      // Conditional fields based on provider type
      if (provider === 'HIKVISION' || provider === 'DAHUA' || provider === 'UBIQUITI') {
        // Cloud Integration - Send API keys with new schema
        payload.cloud_provider = provider.toLowerCase(); // 'hikvision', 'dahua', or 'ubiquiti'
        payload.cloud_api_key_encrypted = formData.api_key.trim(); // Backend will encrypt
        payload.cloud_api_secret_encrypted = formData.app_secret.trim(); // Backend will encrypt
        payload.cloud_sync_enabled = true;
        
        // HikVision-specific verification code for EZOPEN plugin
        if (provider === 'HIKVISION' && formData.verification_code) {
          payload.cloud_verification_code_encrypted = formData.verification_code.trim(); // Backend will encrypt
        }
        
        // Note: nvr_username, nvr_password_encrypted, external_hostname can still be set for external NVR access
        // This allows cloud locations to access NVR ISAPI for metadata extraction and playback
      } else if (provider === 'CUSTOM_NVR') {
        // Custom NVR - null out cloud fields
        payload.cloud_provider = null;
        payload.cloud_sync_enabled = false;
        // Custom NVR - Send NVR configuration
        if (formData.nvr_ip) payload.nvr_ip = formData.nvr_ip;
        if (formData.nvr_port) {
          const port = parseInt(formData.nvr_port);
          if (!isNaN(port)) payload.nvr_port = port;
        }
        if (formData.nvr_rtsp_port) {
          const rtspPort = parseInt(formData.nvr_rtsp_port);
          if (!isNaN(rtspPort)) payload.nvr_rtsp_port = rtspPort;
        }
        if (formData.nvr_username) payload.nvr_username = formData.nvr_username;
        if (formData.nvr_password_encrypted) payload.nvr_password_encrypted = formData.nvr_password_encrypted;
        if (formData.nvr_type) payload.nvr_type = formData.nvr_type;
        if (formData.nvr_model) payload.nvr_model = formData.nvr_model;
        if (formData.nvr_channel_count) {
          const channels = parseInt(formData.nvr_channel_count);
          if (!isNaN(channels)) payload.nvr_channel_count = channels;
        }
        // Explicitly set cloud API keys to null for custom NVR
        payload.api_key = null;
        payload.app_secret = null;
      }

      // Transcoding Configuration
      payload.transcoding_enabled = formData.transcoding_enabled;
      if (formData.transcoding_enabled) {
        if (formData.transcoding_device_ip) payload.transcoding_device_ip = formData.transcoding_device_ip;
        if (formData.transcoding_device_port) {
          const port = parseInt(formData.transcoding_device_port);
          if (!isNaN(port)) payload.transcoding_device_port = port;
        }
        if (formData.transcoding_device_api_port) {
          const apiPort = parseInt(formData.transcoding_device_api_port);
          if (!isNaN(apiPort)) payload.transcoding_device_api_port = apiPort;
        }
        if (formData.transcoding_device_username) payload.transcoding_device_username = formData.transcoding_device_username;
        if (formData.transcoding_device_password_encrypted) payload.transcoding_device_password_encrypted = formData.transcoding_device_password_encrypted;
      }

      // External Access Configuration
      payload.external_access_enabled = formData.external_access_enabled;
      if (formData.external_access_enabled) {
        payload.external_http_port = formData.external_http_port ? Number(formData.external_http_port) : null;
        if (formData.external_hostname) payload.external_hostname = formData.external_hostname;
        if (formData.nvr_username) payload.nvr_username = formData.nvr_username;
        if (formData.nvr_password_encrypted) payload.nvr_password_encrypted = formData.nvr_password_encrypted;
        if (formData.external_transcoding_port) {
          const port = parseInt(formData.external_transcoding_port);
          if (!isNaN(port)) payload.external_transcoding_port = port;
        }
        if (formData.external_rtsp_port) {
          const port = parseInt(formData.external_rtsp_port);
          if (!isNaN(port)) payload.external_rtsp_port = port;
        }
      }

      // Stream Authentication Configuration
      payload.stream_auth_enabled = formData.stream_auth_enabled;
      if (formData.stream_auth_enabled) {
        if (formData.location_identifier) payload.location_identifier = formData.location_identifier;
        if (formData.stream_auth_port) {
          const port = parseInt(formData.stream_auth_port);
          if (!isNaN(port)) payload.stream_auth_port = port;
        }
        if (formData.stream_shared_secret) payload.stream_shared_secret = formData.stream_shared_secret;
      }

      // Settings
      payload.ycl_events_enabled = formData.ycl_events_enabled;
      payload.ycl_entry_logging = formData.ycl_entry_logging;
      payload.ycl_defaut_location = formData.ycl_defaut_location;
      payload.status = formData.status;
      payload.location_timezone = formData.location_timezone;

      const response = await locationService.createLocation(payload);
      
      // Check for cloud sync results
      if (response.cloud_sync) {
        const { cameras_synced, cameras_failed, success: syncSuccess, message } = response.cloud_sync;
        
        if (syncSuccess && cameras_synced > 0) {
          const providerName = provider === 'HIKVISION' ? 'HikVision' : provider === 'DAHUA' ? 'Dahua' : 'Ubiquiti';
          setSuccess(
            `Location created successfully! 🎉 Automatically synced ${cameras_synced} camera${cameras_synced > 1 ? 's' : ''} from ${providerName} Cloud.`
          );
          // Navigate after showing success for 3 seconds
          setTimeout(() => navigate('/locations'), 3000);
        } else if (!syncSuccess) {
          setSuccess(`Location created successfully, but camera sync failed: ${message || 'Unknown error'}`);
          setTimeout(() => navigate('/locations'), 4000);
        } else {
          setSuccess(`Location created successfully! ${message || 'No cameras found to sync.'}`);
          setTimeout(() => navigate('/locations'), 3000);
        }
      } else {
        // No cloud sync (custom NVR)
        setSuccess('Location created successfully!');
        setTimeout(() => navigate('/locations'), 2000);
      }
    } catch (err) {
      console.error('Create location error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create location';
      setError(errorMessage);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/locations')}
            >
              Back to Locations
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-text-primary">Create Location</h1>
              <p className="text-text-muted">Add a new camera location</p>
            </div>
          </div>
        </div>

        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
        {success && <Alert variant="success" message={success} onClose={() => setSuccess(null)} />}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Provider Selection */}
          <Card padding="lg" className="bg-gradient-to-br from-accent-primary/5 to-accent-secondary/5 border-2 border-accent-primary/20">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-accent-primary/10 rounded-lg">
                {provider === 'CUSTOM_NVR' ? <Server className="w-5 h-5 text-accent-primary" /> : <Cloud className="w-5 h-5 text-accent-primary" />}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary">Integration Type</h3>
                <p className="text-sm text-text-muted mt-1">
                  Choose between cloud-managed cameras or local NVR configuration
                </p>
              </div>
            </div>
            <Select
              label="Provider *"
              options={[
                { value: 'CUSTOM_NVR', label: '🖥️ Custom NVR (Local IP)' },
                { value: 'HIKVISION', label: '☁️ HikVision Cloud (Hik-Connect)' },
                { value: 'DAHUA', label: '☁️ Dahua Cloud' },
                { value: 'UBIQUITI', label: '☁️ Ubiquiti Cloud (UniFi Protect)' },
              ]}
              value={provider}
              onChange={(e) => setProvider(e.target.value as ProviderType)}
              fullWidth
            />
            {(provider === 'HIKVISION' || provider === 'DAHUA' || provider === 'UBIQUITI') && (
              <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-200">
                  <strong>Cloud Provider Selected:</strong> Cameras will be automatically synced from your {provider === 'HIKVISION' ? 'HikVision' : provider === 'DAHUA' ? 'Dahua' : 'Ubiquiti'} cloud account after location creation.
                </p>
              </div>
            )}
          </Card>

          {/* Basic Information */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Location Name *"
                  placeholder="e.g., OBX Office"
                  value={formData.ycl_name}
                  onChange={(e) => setFormData({ ...formData, ycl_name: e.target.value })}
                  error={formErrors.ycl_name}
                  fullWidth
                />
                <Input
                  label="Location Code *"
                  placeholder="e.g., OBX001"
                  value={formData.ycl_code}
                  onChange={(e) => setFormData({ ...formData, ycl_code: e.target.value })}
                  error={formErrors.ycl_code}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Location Type *"
                  placeholder="e.g., Warehouse, Office, Parking Lot"
                  value={formData.ycl_type}
                  onChange={(e) => setFormData({ ...formData, ycl_type: e.target.value })}
                  error={formErrors.ycl_type}
                  fullWidth
                />
                <Input
                  label="Company ID"
                  type="number"
                  placeholder="e.g., 1"
                  value={formData.ycl_company}
                  onChange={() => {}}
                  disabled
                  fullWidth
                />
              </div>
              {provider === 'CUSTOM_NVR' && (
                <Textarea
                  label="Description"
                  placeholder="Enter location description"
                  value={formData.ycl_description}
                  onChange={(e) => setFormData({ ...formData, ycl_description: e.target.value })}
                  fullWidth
                />
              )}
            </div>
          </Card>

          {/* Cloud Integration Credentials (HIKVISION, DAHUA, or UBIQUITI only) */}
          {(provider === 'HIKVISION' || provider === 'DAHUA' || provider === 'UBIQUITI') && (
            <Card padding="lg" className="border-l-4 border-l-accent-primary">
              <div className="flex items-start gap-3 mb-4">
                <Cloud className="w-5 h-5 text-accent-primary mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">
                    {provider === 'HIKVISION' ? 'HikVision' : provider === 'DAHUA' ? 'Dahua' : 'Ubiquiti'} Cloud Credentials
                  </h3>
                  <p className="text-sm text-text-muted mt-1">
                    Enter your cloud API credentials to auto-sync cameras
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <Input
                  label="API Key / App Key *"
                  placeholder={provider === 'HIKVISION' ? 'Enter your HikVision AppKey' : provider === 'DAHUA' ? 'Enter your Dahua AppKey' : 'Enter your Ubiquiti API Key'}
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  error={formErrors.api_key}
                  fullWidth
                />
                <Input
                  label="App Secret / Secret Key *"
                  type="password"
                  placeholder={provider === 'HIKVISION' ? 'Enter your HikVision AppSecret' : provider === 'DAHUA' ? 'Enter your Dahua AppSecret' : 'Enter your Ubiquiti Secret'}
                  value={formData.app_secret}
                  onChange={(e) => setFormData({ ...formData, app_secret: e.target.value })}
                  error={formErrors.app_secret}
                  fullWidth
                />
                
                {/* HikVision Verification Code (for EZOPEN plugin) */}
                {provider === 'HIKVISION' && (
                  <Input
                    label="Verification Code *"
                    placeholder="Enter verification code for live view"
                    value={formData.verification_code}
                    onChange={(e) => setFormData({ ...formData, verification_code: e.target.value })}
                    error={formErrors.verification_code}
                    fullWidth
                  />
                )}
                
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm text-yellow-200">
                    <strong>Note:</strong> These credentials will be used to fetch all cameras from your {provider === 'HIKVISION' ? 'HikVision Hik-Connect' : provider === 'DAHUA' ? 'Dahua' : 'Ubiquiti UniFi Protect'} account and sync them to this location automatically.
                  </p>
                  {provider === 'HIKVISION' && (
                    <p className="text-sm text-yellow-200 mt-2">
                      <strong>Verification Code:</strong> This is used by the EZOPEN plugin for live camera viewing. You can find this in your HikVision device settings.
                    </p>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Address Information (Custom NVR only) */}
          {provider === 'CUSTOM_NVR' && (
            <Card padding="lg">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Address Information</h3>
            <div className="space-y-4">
              <Input
                label="Street Address"
                placeholder="e.g., 123 Main Street"
                value={formData.ycl_address}
                onChange={(e) => setFormData({ ...formData, ycl_address: e.target.value })}
                fullWidth
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="City"
                  placeholder="e.g., Outer Banks"
                  value={formData.ycl_city}
                  onChange={(e) => setFormData({ ...formData, ycl_city: e.target.value })}
                  fullWidth
                />
                <Input
                  label="Province/State"
                  placeholder="e.g., NC"
                  value={formData.ycl_province}
                  onChange={(e) => setFormData({ ...formData, ycl_province: e.target.value })}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Postal Code"
                  placeholder="e.g., 27948"
                  value={formData.ycl_postal_code}
                  onChange={(e) => setFormData({ ...formData, ycl_postal_code: e.target.value })}
                  fullWidth
                />
                <Input
                  label="Caller ID"
                  placeholder="e.g., +1-252-123-4567"
                  value={formData.ycl_caller_id}
                  onChange={(e) => setFormData({ ...formData, ycl_caller_id: e.target.value })}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Latitude"
                  type="number"
                  step="any"
                  placeholder="e.g., 35.8"
                  value={formData.ycl_latitude}
                  onChange={(e) => setFormData({ ...formData, ycl_latitude: e.target.value })}
                  fullWidth
                />
                <Input
                  label="Longitude"
                  type="number"
                  step="any"
                  placeholder="e.g., -75.5"
                  value={formData.ycl_longitude}
                  onChange={(e) => setFormData({ ...formData, ycl_longitude: e.target.value })}
                  fullWidth
                />
              </div>
            </div>
          </Card>
          )}

          {/* NVR Configuration (Custom NVR only) */}
          {provider === 'CUSTOM_NVR' && (
            <div className="space-y-6">
              <Card padding="lg" className="border-l-4 border-l-blue-500">
                <div className="flex items-start gap-3 mb-4">
                  <Server className="w-5 h-5 text-blue-500 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">NVR Configuration</h3>
                    <p className="text-sm text-text-muted mt-1">
                      Configure your local Network Video Recorder
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="NVR IP Address *"
                  placeholder="e.g., 192.168.1.100"
                  value={formData.nvr_ip}
                  onChange={(e) => setFormData({ ...formData, nvr_ip: e.target.value })}
                  error={formErrors.nvr_ip}
                  fullWidth
                />
                <Input
                  label="NVR Port (ISAPI)"
                  type="number"
                  placeholder="e.g., 80"
                  value={formData.nvr_port}
                  onChange={(e) => setFormData({ ...formData, nvr_port: e.target.value })}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="NVR RTSP Port"
                  type="number"
                  placeholder="e.g., 554"
                  value={formData.nvr_rtsp_port}
                  onChange={(e) => setFormData({ ...formData, nvr_rtsp_port: e.target.value })}
                  fullWidth
                />
                <Input
                  label="NVR Username *"
                  placeholder="e.g., admin"
                  value={formData.nvr_username}
                  onChange={(e) => setFormData({ ...formData, nvr_username: e.target.value })}
                  error={formErrors.nvr_username}
                  fullWidth
                />
                <Input
                  label="NVR Password *"
                  type="password"
                  placeholder="Enter NVR password"
                  value={formData.nvr_password_encrypted}
                  onChange={(e) => setFormData({ ...formData, nvr_password_encrypted: e.target.value })}
                  error={formErrors.nvr_password_encrypted}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="NVR Type"
                  placeholder="e.g., Hikvision, Dahua"
                  value={formData.nvr_type}
                  onChange={(e) => setFormData({ ...formData, nvr_type: e.target.value })}
                  fullWidth
                />
                <Input
                  label="NVR Model"
                  placeholder="e.g., DS-7616NI-K2"
                  value={formData.nvr_model}
                  onChange={(e) => setFormData({ ...formData, nvr_model: e.target.value })}
                  fullWidth
                />
              </div>
              <Input
                label="NVR Channel Count"
                type="number"
                placeholder="e.g., 16"
                value={formData.nvr_channel_count}
                onChange={(e) => setFormData({ ...formData, nvr_channel_count: e.target.value })}
                fullWidth
              />
                </div>
              </Card>
              
              {/* Transcoding Configuration */}
              <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Transcoding Configuration</h3>
                <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.transcoding_enabled}
                  onChange={(e) => setFormData({ ...formData, transcoding_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable Transcoding</span>
              </label>
              {formData.transcoding_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Transcoding Device IP"
                      placeholder="e.g., 192.168.1.200"
                      value={formData.transcoding_device_ip}
                      onChange={(e) => setFormData({ ...formData, transcoding_device_ip: e.target.value })}
                      fullWidth
                    />
                    <Input
                      label="Device Port"
                      type="number"
                      placeholder="e.g., 8080"
                      value={formData.transcoding_device_port}
                      onChange={(e) => setFormData({ ...formData, transcoding_device_port: e.target.value })}
                      fullWidth
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="API Port"
                      type="number"
                      placeholder="e.g., 8000"
                      value={formData.transcoding_device_api_port}
                      onChange={(e) => setFormData({ ...formData, transcoding_device_api_port: e.target.value })}
                      fullWidth
                    />
                    <Input
                      label="Username"
                      placeholder="e.g., admin"
                      value={formData.transcoding_device_username}
                      onChange={(e) => setFormData({ ...formData, transcoding_device_username: e.target.value })}
                      fullWidth
                    />
                  </div>
                  <Input
                    label="Password"
                    type="password"
                    placeholder="Enter transcoding device password"
                    value={formData.transcoding_device_password_encrypted}
                    onChange={(e) => setFormData({ ...formData, transcoding_device_password_encrypted: e.target.value })}
                    fullWidth
                  />
                </>
                  )}
                </div>
              </Card>

              {/* Stream Authentication Configuration (Custom NVR only) */}
              <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Stream Authentication (JWT)</h3>
                <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.stream_auth_enabled}
                  onChange={(e) => setFormData({ ...formData, stream_auth_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable JWT Stream Authentication</span>
              </label>
              <p className="text-sm text-text-secondary">
                Requires camera streams to use JWT tokens. Enable this for secure remote access.
              </p>
              {formData.stream_auth_enabled && (
                <>
                  <Input
                    label="Location Identifier"
                    placeholder="e.g., secureview-n95-001"
                    value={formData.location_identifier}
                    onChange={(e) => setFormData({ ...formData, location_identifier: e.target.value })}
                    fullWidth
                    helperText="Must match the LOCATION_ID on your edge device"
                  />
                  <Input
                    label="Stream Auth Port"
                    type="number"
                    placeholder="8000"
                    value={formData.stream_auth_port}
                    onChange={(e) => setFormData({ ...formData, stream_auth_port: e.target.value })}
                    fullWidth
                    helperText="Port where the JWT auth gateway is running (default: 8000)"
                  />
                  <Input
                    label="Shared Secret Key"
                    type="password"
                    placeholder="Enter secret key"
                    value={formData.stream_shared_secret}
                    onChange={(e) => setFormData({ ...formData, stream_shared_secret: e.target.value })}
                    fullWidth
                    helperText="Must match SHARED_SECRET on edge device. Required for JWT token validation."
                  />
                    </>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* External Access Configuration (All Provider Types) */}
          <Card padding="lg" className="border-l-4 border-l-green-500">
            <div className="flex items-start gap-3 mb-4">
              <ExternalLink className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-text-primary">External NVR Access (Optional)</h3>
                <p className="text-sm text-text-muted mt-1">
                  {provider === 'CUSTOM_NVR' 
                    ? 'Configure DDNS/WAN access for remote NVR connectivity'
                    : 'Configure NVR ISAPI access for metadata extraction, playback, and alarm event details'}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.external_access_enabled}
                  onChange={(e) => setFormData({ ...formData, external_access_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable External NVR Access</span>
              </label>
              
              {provider !== 'CUSTOM_NVR' && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-200">
                    <strong>Cloud + NVR Integration:</strong> Enable this if your cloud-synced cameras also have a local NVR that can be accessed externally. 
                    This allows the system to fetch intelligent metadata (behavior analysis, face detection, intrusion zones) and accurate playback timestamps from the NVR's ISAPI endpoints.
                  </p>
                </div>
              )}
              
              {formData.external_access_enabled && (
                <>
                  <div className="space-y-2">
                    <Input
                      label="External Hostname"
                      placeholder="e.g., office.ddns.net or office.ddns.net:8093"
                      value={formData.external_hostname}
                      onChange={(e) => setFormData({ ...formData, external_hostname: e.target.value })}
                      fullWidth
                      helperText="DDNS hostname, public IP or HTTP/HTTPS address. An inline port takes priority."
                      error={formErrors.external_hostname}
                    />
                    {formData.external_hostname && (
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<ExternalLink size={14} />}
                        onClick={() => {
                          try { window.open(nvrHttpUrl(formData.external_hostname, formData.external_http_port, formData.nvr_port), '_blank', 'noopener,noreferrer'); }
                          catch (error) { setError(error instanceof Error ? error.message : 'Check the NVR address.'); }
                        }}
                        type="button"
                      >
                        Test External Access
                      </Button>
                    )}
                  </div>
                  <Input label="NVR HTTP/ISAPI Port" type="number" min={1} max={65535} step={1}
                    placeholder="e.g., 8093" value={formData.external_http_port}
                    onChange={(e) => setFormData({ ...formData, external_http_port: e.target.value })} fullWidth
                    helperText="Port forwarded to the NVR HTTP/ISAPI service for event recordings. A port in External Hostname takes priority." />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="NVR Username"
                      placeholder="e.g., admin"
                      value={formData.nvr_username}
                      onChange={(e) => setFormData({ ...formData, nvr_username: e.target.value })}
                      fullWidth
                      helperText={provider === 'CUSTOM_NVR' ? 'Username for remote NVR' : 'Username for NVR ISAPI access'}
                    />
                    <Input
                      label="NVR Password"
                      type="password"
                      placeholder="Enter NVR password"
                      value={formData.nvr_password_encrypted}
                      onChange={(e) => setFormData({ ...formData, nvr_password_encrypted: e.target.value })}
                      fullWidth
                      helperText={provider === 'CUSTOM_NVR' ? 'Password for remote NVR' : 'Password for NVR ISAPI access'}
                    />
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card padding="lg">
            <details>
              <summary className="cursor-pointer text-sm font-semibold text-text-primary">Other streaming services (optional)</summary>
              <p className="my-3 text-sm text-text-secondary">These ports belong to streaming and transcoding services. Event recording downloads use the NVR HTTP/ISAPI port above.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="External Transcoding Port"
                        type="number"
                        placeholder="e.g., 1984"
                        value={formData.external_transcoding_port}
                        onChange={(e) => setFormData({ ...formData, external_transcoding_port: e.target.value })}
                        fullWidth
                      />
                      <Input
                        label="External RTSP Port"
                        type="number"
                        placeholder="e.g., 8554"
                        value={formData.external_rtsp_port}
                        onChange={(e) => setFormData({ ...formData, external_rtsp_port: e.target.value })}
                        fullWidth
                      />
                    </div>
            </details>
          </Card>

          {/* Settings */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Settings</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.ycl_events_enabled}
                  onChange={(e) => setFormData({ ...formData, ycl_events_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable Events</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.ycl_entry_logging}
                  onChange={(e) => setFormData({ ...formData, ycl_entry_logging: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable Entry Logging</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Active Status</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.ycl_defaut_location}
                  onChange={(e) => setFormData({ ...formData, ycl_defaut_location: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Default Location for Company</span>
              </label>
            </div>
          </Card>

          {/* Timezone Configuration */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Location Timezone</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  NVR Timezone
                </label>
                <Select
                  value={formData.location_timezone || 'America/Toronto'}
                  onChange={(e) => setFormData({ ...formData, location_timezone: e.target.value })}
                  options={[
                    { label: 'Eastern Time (Toronto, New York, Miami)', value: 'America/Toronto' },
                    { label: 'Central Time (Chicago, Dallas, Mexico City)', value: 'America/Chicago' },
                    { label: 'Mountain Time (Denver, Phoenix)', value: 'America/Denver' },
                    { label: 'Pacific Time (Los Angeles, Vancouver)', value: 'America/Los_Angeles' },
                    { label: 'Alaska Time (Anchorage)', value: 'America/Anchorage' },
                    { label: 'Hawaii Time (Honolulu)', value: 'Pacific/Honolulu' },
                    { label: 'Atlantic Time (Halifax)', value: 'America/Halifax' },
                    { label: 'UTC (Universal Time)', value: 'UTC' },
                    { label: 'London (GMT/BST)', value: 'Europe/London' },
                    { label: 'Paris (CET/CEST)', value: 'Europe/Paris' },
                    { label: 'Dubai (GST)', value: 'Asia/Dubai' },
                    { label: 'India (IST)', value: 'Asia/Kolkata' },
                    { label: 'China (CST)', value: 'Asia/Shanghai' },
                    { label: 'Singapore (SGT)', value: 'Asia/Singapore' },
                    { label: 'Japan (JST)', value: 'Asia/Tokyo' },
                    { label: 'Australia Sydney (AEDT/AEST)', value: 'Australia/Sydney' },
                  ]}
                />
                <p className="text-xs text-text-secondary mt-1">
                  Select the timezone where your NVR is located. Events will be stored in this timezone for accurate playback and metadata retrieval. Automatically handles daylight saving time.
                </p>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 sticky bottom-0 bg-surface-primary p-4 border-t border-border">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/locations')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              leftIcon={<Save size={18} />}
            >
              Create Location
            </Button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default CreateLocationPage;
